import * as p from '@clack/prompts'

import { parseDotenvFile, pullVercelEnvValue } from './env-pull.js'
import { parseVercelJson, runVercel } from './runner.js'
import type { NeonProvisionResult, VercelRunner } from './types.js'

/** Non-interactive provisioning can't wait on a human; keep it bounded. */
const PROVISION_TIMEOUT_MS = 180_000
/** Interactive provisioning waits on the user clicking through Vercel's prompts. */
const INTERACTIVE_PROVISION_TIMEOUT_MS = 600_000

interface NeonResourceMeta {
  name?: string
  resourceName?: string
}

/**
 * Interactively provision a Neon Postgres database via `vercel integration add
 * neon` with inherited stdio. This is the primary path when a human is present:
 * the user accepts marketplace terms and picks the Free plan/region in Vercel's
 * own prompts (things that can't be done non-interactively on a fresh account).
 * Uses `--no-env-pull` and then pulls the DATABASE_URL into a temp file so
 * BetterStart's `.env.local` is never overwritten. Callers must stop any active
 * spinner first — inherited stdio and a clack spinner fight over the TTY.
 */
export async function provisionNeonInteractive(
  runner: VercelRunner,
  cwd: string,
  options: { plan?: string; env?: NodeJS.ProcessEnv }
): Promise<NeonProvisionResult> {
  const addArgs = ['integration', 'add', 'neon', '--no-env-pull']
  if (options.plan) addArgs.push('--plan', options.plan)

  const add = await runVercel(runner, addArgs, {
    cwd,
    mode: 'inherit',
    timeoutMs: INTERACTIVE_PROVISION_TIMEOUT_MS,
    env: options.env
  })

  if (!add.success) {
    return { failure: add.timedOut ? 'timeout' : 'provision-failed' }
  }

  const pullSpinner = p.spinner()
  pullSpinner.start('Retrieving the database connection string')
  const databaseUrl = await pullDatabaseUrl(runner, cwd, options.env)
  if (!databaseUrl) {
    pullSpinner.stop('No DATABASE_URL came back from Vercel')
    return { failure: 'env-pull-empty' }
  }
  pullSpinner.stop('Captured the Neon DATABASE_URL')

  return { databaseUrl }
}

/**
 * Non-interactive provisioning for `--yes`/CI with a `VERCEL_TOKEN`. Runs
 * `integration add neon --format json`; on a fresh account this can fail when
 * marketplace terms or a plan selection are required (no human to confirm), so
 * the captured output is returned as `detail` for debugging. A `--plan` id is
 * usually required for this path to succeed unattended.
 */
export async function provisionNeon(
  runner: VercelRunner,
  cwd: string,
  options: { plan?: string; env?: NodeJS.ProcessEnv }
): Promise<NeonProvisionResult> {
  const addArgs = ['integration', 'add', 'neon', '--no-env-pull', '--format', 'json']
  if (options.plan) addArgs.push('--plan', options.plan)

  const spinner = p.spinner()
  spinner.start('Provisioning Neon Postgres via Vercel')

  const add = await runVercel(runner, addArgs, {
    cwd,
    mode: 'capture',
    timeoutMs: PROVISION_TIMEOUT_MS,
    env: options.env
  })

  if (!add.success) {
    spinner.stop('Neon provisioning did not complete')
    const combined = `${add.stdout}\n${add.stderr}`.trim()
    const detail = combined || undefined
    if (/terms/i.test(combined)) return { failure: 'terms-required', detail }
    if (/claim/i.test(combined)) return { failure: 'claim-required', detail }
    return { failure: add.timedOut ? 'timeout' : 'provision-failed', detail }
  }

  const meta = parseVercelJson<NeonResourceMeta>(add.stdout)
  const resourceName = meta?.resourceName ?? meta?.name

  spinner.message('Retrieving the database connection string')
  const databaseUrl = await pullDatabaseUrl(runner, cwd, options.env)
  if (!databaseUrl) {
    spinner.stop('No DATABASE_URL came back from Vercel')
    return { resourceName, failure: 'env-pull-empty' }
  }

  spinner.stop('Captured the Neon DATABASE_URL')
  return { databaseUrl, resourceName }
}

/** Pull the development env into a temp dotenv, extract a Postgres URL, delete it. */
function pullDatabaseUrl(
  runner: VercelRunner,
  cwd: string,
  env?: NodeJS.ProcessEnv
): Promise<string | undefined> {
  return pullVercelEnvValue(runner, cwd, readDbUrlFromDotenv, env)
}

/**
 * Extract the best Postgres connection string from a pulled dotenv file. The
 * Neon integration writes `DATABASE_URL` plus pooled variants (`POSTGRES_URL`,
 * etc.); prefer `DATABASE_URL`, then `POSTGRES_URL`, then any `*DATABASE_URL`,
 * then any `postgres://` value.
 */
export function readDbUrlFromDotenv(filePath: string): string | undefined {
  const vars = parseDotenvFile(filePath)

  const isPg = (v: string | undefined): v is string =>
    !!v && (v.startsWith('postgres://') || v.startsWith('postgresql://'))

  if (isPg(vars.get('DATABASE_URL'))) return vars.get('DATABASE_URL')
  if (isPg(vars.get('POSTGRES_URL'))) return vars.get('POSTGRES_URL')
  for (const [key, value] of vars) {
    if (/DATABASE_URL$/.test(key) && isPg(value)) return value
  }
  for (const value of vars.values()) {
    if (isPg(value)) return value
  }
  return undefined
}
